import { useEffect, useRef, useState } from 'react';
import { Badge, BlockStack, Button, Card, InlineStack, SkeletonBodyText, Text, TextField } from '@shopify/polaris';
import { CheckCircleIcon, ClipboardIcon, ExternalIcon } from '@shopify/polaris-icons';
import { useSelector } from 'react-redux';
import EditTrackingUrlButton from '@/components/EditTrackingUrlButton';
import useTrackingPageUrl from '@/hooks/useTrackingPageUrl';
import { useGetAppStatusQuery } from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import { TRACKING_PAGE_ACCESS_CARD as TEXT } from './config';

const COPIED_RESET_MS = 2000;

interface ICopyButtonProps {
  value: string;
  copyLabel: string;
  copiedLabel: string;
}

const CopyButton = ({ value, copyLabel, copiedLabel }: ICopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setIsCopied(false), COPIED_RESET_MS);
    } catch {
      // clipboard không khả dụng — field read-only vẫn cho copy thủ công (R02)
    }
  };

  return (
    <Button
      onClick={handleCopy}
      accessibilityLabel={isCopied ? copiedLabel : copyLabel}
      icon={isCopied ? CheckCircleIcon : ClipboardIcon}
    />
  );
};

interface ITrackingPageAccessCardProps {
  /** Hiển thị Option 3 · Add it manually (snippet embed). Mặc định false — chỉ Settings bật. */
  showManual?: boolean;
}

const TrackingPageAccessCard = ({ showManual = false }: ITrackingPageAccessCardProps) => {
  const { shop } = useSelector(tokenSelector);
  const { data: appStatusData, isLoading } = useGetAppStatusQuery();

  const { url: proxyUrl } = useTrackingPageUrl();
  const appBlockStatus = appStatusData?.data?.appStatus ?? false;

  const handleOpenThemeEditor = () => {
    const appExtensionId = process.env.REACT_APP_THEME_APP_EXTENSION_ID;
    if (!shop || !appExtensionId) return;
    // App block deep link (same as Settings → Order Lookup Widget): mở theme editor
    // và thêm app block vào template main — KHÔNG dùng context=apps/activateAppId (app embed).
    window.open(
      `https://${shop}/admin/themes/current/editor?addAppBlockId=${appExtensionId}/order-tracking&target=main`,
      '_blank',
    );
  };

  return (
    <Card>
      <BlockStack gap="300">
        <BlockStack gap="050">
          <Text as="h2" variant="headingSm">
            {TEXT.title}
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            {TEXT.subtitle}
          </Text>
        </BlockStack>

        <BlockStack gap="100">
          <InlineStack gap="150" blockAlign="center">
            <Text as="span" variant="bodySm" fontWeight="medium" tone="subdued">
              {TEXT.option1Label}
            </Text>
            <Badge tone="success">{TEXT.activeLabel}</Badge>
          </InlineStack>
          <InlineStack gap="100" blockAlign="center" wrap={false}>
            <div style={{ flex: 1 }}>
              <TextField label={TEXT.trackingPageLinkLabel} labelHidden value={proxyUrl} autoComplete="off" readOnly />
            </div>
            <CopyButton value={proxyUrl} copyLabel={TEXT.copyLabel} copiedLabel={TEXT.copiedLabel} />
            <Button url={proxyUrl} external accessibilityLabel={TEXT.openLabel} icon={ExternalIcon} />
            <EditTrackingUrlButton />
          </InlineStack>
        </BlockStack>

        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="150" blockAlign="center">
            <Text as="span" variant="bodySm" fontWeight="medium" tone="subdued">
              {TEXT.option2Label}
            </Text>
            {isLoading ? (
              <div style={{ width: 48 }}>
                <SkeletonBodyText lines={1} />
              </div>
            ) : (
              <Badge tone={appBlockStatus ? 'success' : undefined}>{appBlockStatus ? 'On' : 'Off'}</Badge>
            )}
          </InlineStack>
          <Button onClick={handleOpenThemeEditor}>{appBlockStatus ? TEXT.manageCta : TEXT.addCta}</Button>
        </InlineStack>

        {showManual && (
          <BlockStack gap="100">
            <Text as="span" variant="bodySm" fontWeight="medium" tone="subdued">
              {TEXT.option3Label}
            </Text>
            <Text as="span" variant="bodySm" tone="subdued">
              {TEXT.option3Description}
            </Text>
            <InlineStack gap="100" blockAlign="center" wrap={false}>
              <div style={{ flex: 1 }}>
                <TextField
                  label={TEXT.embedCodeLabel}
                  labelHidden
                  value={TEXT.embedCode}
                  autoComplete="off"
                  readOnly
                />
              </div>
              <CopyButton value={TEXT.embedCode} copyLabel={TEXT.copyCodeLabel} copiedLabel={TEXT.copiedLabel} />
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
};

export default TrackingPageAccessCard;
